import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Pencil, RefreshCw, Search, UserCheck, Users } from 'lucide-react';
import * as hr from '../api';
import { useHR, useLookup, clearLookup } from '../HRContext';
import { errorText } from '../../utils';
import Modal from '../components/Modal';
import { Input } from '../components/Form';
import StatusPill from '../components/StatusPill';
import { LcKpis } from '../components/Charts';
import { EMPLOYMENT_TYPES, GENDERS, SALARY_TYPES, has, initials, titleCase } from '../format';
import { hrSubUrl } from '../catalog';
import { BANKS, PFAS } from './AddEmployee';
import Loader from '../components/Loader';
import { Select } from '../components/Controls';
import Pagination from '../../components/requisition/Pagination';

// Every user account is also an employee. Staff imported from user accounts arrive active but with gaps
// (no hire date, bank or statutory details …). This page lists what each record is missing and lets HR
// fill the essentials in one form; the full record stays in Add Employee.

// [key, label, present(employee)]
export const CHECKS = [
  ['hire_date', 'Hire Date', e => has(e.hire_date)],
  ['department', 'Department', e => has(e.department_name)],
  ['designation', 'Designation', e => has(e.designation_title)],
  ['site', 'Primary Site', e => has(e.site_name)],
  ['gender', 'Gender', e => has(e.gender)],
  ['phone', 'Phone', e => has(e.work_phone)],
  ['email', 'Work Email', e => has(e.work_email)],
  ['salary', 'Base Salary', e => Number(e.basic_salary) > 0],
  ['bank', 'Bank Account', e => has(e.bank_account_number)],
  ['tin', 'Tax ID (TIN)', e => has(e.tax_identification_number)],
  ['pension', 'Pension RSA PIN', e => has(e.pension_rsa_pin)],
];
export const completeness = e => {
  const missing = CHECKS.filter(([, , ok]) => !ok(e));
  return { missing, percent: Math.round(((CHECKS.length - missing.length) / CHECKS.length) * 100) };
};

const opts = list => list.map(v => ({ value: v, label: v }));
const FIELDS = {
  hire_date: { label: 'Date of Employment', type: 'date' },
  employment_type: { label: 'Employment Type', type: 'select', options: EMPLOYMENT_TYPES, noBlank: true },
  designation_id: { label: 'Designation', type: 'ref', ref: 'designations' },
  primary_site_id: { label: 'Primary Project Site', type: 'ref', ref: 'sites' },
  salary_type: { label: 'Salary Type', type: 'select', options: SALARY_TYPES, noBlank: true },
  basic_salary: { label: 'Base Salary (monthly)', type: 'money' },
  work_phone: { label: 'Phone Number' },
  work_email: { label: 'Work Email', type: 'email' },
  gender: { label: 'Gender', type: 'select', options: GENDERS, placeholder: 'Select gender' },
  date_of_birth: { label: 'Date of Birth', type: 'date' },
  bank_name: { label: 'Bank Name', type: 'select', options: opts(BANKS), placeholder: 'Select bank' },
  bank_account_name: { label: 'Account Name' },
  bank_account_number: { label: 'Account Number', maxLength: 10 },
  tax_identification_number: { label: 'Tax Identification Number (TIN)' },
  pension_fund_administrator: { label: 'Pension Provider / PFA', type: 'select', options: opts(PFAS), placeholder: 'Select PFA' },
  pension_rsa_pin: { label: 'Pension ID / RSA Number' },
  nhf_number: { label: 'NHF Number' },
};
Object.entries(FIELDS).forEach(([n, f]) => { f.name = n; });
const MASTER = ['hire_date', 'employment_type', 'designation_id', 'primary_site_id', 'salary_type', 'basic_salary', 'work_phone', 'work_email'];
const PERSONAL = ['gender', 'date_of_birth'];
const BANK = ['bank_name', 'bank_account_name', 'bank_account_number', 'tax_identification_number', 'pension_fund_administrator', 'pension_rsa_pin', 'nhf_number'];
const pick = (names, d) => Object.fromEntries(names.map(n => [n, d[n] === '' || d[n] === undefined ? null : n === 'basic_salary' ? Number(d[n]) : d[n]]));

function QuickComplete({ employeeId, onClose, onSaved }) {
  const { notify } = useHR();
  const departments = useLookup('departments') || [];
  const [d, setD] = useState(null);
  const [depts, setDepts] = useState([]);
  const [primary, setPrimary] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    hr.getEmployee(employeeId).then(e => {
      setD({ ...e, ...(e.personal_info || {}), ...(e.statutory_and_bank || {}), id: e.id, basic_salary: Number(e.basic_salary) || '' });
      const ids = (e.departments || []).map(x => String(x.id));
      setDepts(ids);
      setPrimary(e.department_id ? String(e.department_id) : ids[0] || '');
    }).catch(err => setError(errorText(err)));
  }, [employeeId]);

  if (!d) return <Modal title="Complete Employee Record" onClose={onClose}>{error ? <div className="error">{error}</div> : <Loader title="Loading employee record…" />}</Modal>;

  const set = (k, v) => setD(o => ({ ...o, [k]: v }));
  const toggleDept = id => setDepts(list => {
    const next = list.includes(id) ? list.filter(x => x !== id) : [...list, id];
    if (!next.includes(primary)) setPrimary(next[0] || '');
    return next;
  });
  const field = n => (
    <div key={n} className={`field ${FIELDS[n].type === 'textarea' ? 'full' : ''}`}>
      <span className="field-label">{FIELDS[n].label}</span>
      <Input field={FIELDS[n]} value={d[n]} onChange={v => set(n, v)} />
    </div>
  );

  const save = async e => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const master = pick(MASTER, d);
      if (master.basic_salary === null) delete master.basic_salary;
      await hr.updateEmployee(d.id, master);
      const personal = pick(PERSONAL, d);
      if (personal.gender || d.personal_info) {
        if (!personal.gender) delete personal.gender;
        await hr.updatePersonalInfo(d.id, personal);
      }
      const bank = pick(BANK, d);
      if (Object.values(bank).some(has)) await hr.updateStatutory(d.id, bank);
      await hr.setEmployeeDepartments(d.id, depts, primary || null);
      clearLookup('employees');
      notify(`${d.full_name}: record updated`);
      onSaved();
    } catch (err) { setError(errorText(err)); }
    setBusy(false);
  };

  return (
    <Modal title={`Complete Record — ${d.full_name}`} onClose={onClose} wide>
      <form onSubmit={save} className="am-form">
        <div className="am-sec"><span className="sec-icon"><Pencil size={16} /></span><b>Employment</b></div>
        <div className="form-grid cols-3">{['hire_date', 'employment_type', 'designation_id', 'primary_site_id', 'salary_type', 'basic_salary'].map(field)}</div>
        <div className="am-sec"><span className="sec-icon"><Users size={16} /></span><b>Departments</b>
          <small className="muted">{d.user_email ? ' · also applied to the linked login (approval routing)' : ''}</small></div>
        <div className="toggle-grid">
          {departments.map(dep => {
            const id = String(dep.id), on = depts.includes(id);
            return (
              <button key={id} type="button" className={`toggle-card ${on ? 'on' : ''}`} onClick={() => toggleDept(id)}>
                <span><b>{dep.name}</b><small>{on ? (primary === id ? 'Primary department' : 'Member') : 'Not a member'}</small></span>
                <span className="switch-ui" aria-hidden="true" />
              </button>
            );
          })}
        </div>
        {depts.length > 1 && (
          <div className="form-grid cols-3">
            <div className="field"><span className="field-label">Primary Department</span>
              <Select value={primary} onChange={ev => setPrimary(ev.target.value)}>
                {depts.map(id => <option key={id} value={id}>{departments.find(x => String(x.id) === id)?.name || id}</option>)}
              </Select></div>
          </div>
        )}
        <div className="am-sec"><span className="sec-icon"><UserCheck size={16} /></span><b>Personal & Contact</b></div>
        <div className="form-grid cols-3">{['gender', 'date_of_birth', 'work_phone', 'work_email'].map(field)}</div>
        <div className="am-sec"><span className="sec-icon"><Pencil size={16} /></span><b>Bank & Statutory</b></div>
        <div className="form-grid cols-3">{BANK.map(field)}</div>
        {error && <div className="error">{error}</div>}
        <div className="form-actions">
          <Link className="btn ghost" to={hrSubUrl('add-employee', { id: d.id })}>Open full record</Link>
          <div className="spacer" />
          <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </Modal>
  );
}

export default function CompleteEmployeeRecords() {
  const { canManage, isSuperAdmin, notify, confirm } = useHR();
  const [data, setData] = useState(null); // { items, total, summary } from the server
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [search, setSearch] = useState(''); // applied only when Search is clicked
  const [missing, setMissing] = useState('');
  const [dept, setDept] = useState('');
  const [onlyIncomplete, setOnlyIncomplete] = useState(true);
  const [page, setPage] = useState({ skip: 0, limit: 10 });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const departments = useLookup('departments') || [];

  // completeness is worked out, filtered and paged on the server (GET /employees/completeness), least complete first
  const params = { search: search || undefined, department_id: dept || undefined, missing: missing || undefined, incomplete_only: onlyIncomplete || undefined };
  const key = JSON.stringify(params);
  const firstPage = () => setPage(p => (p.skip ? { ...p, skip: 0 } : p));
  const filter = set => v => { set(v); firstPage(); };
  const latest = useRef(0);
  const load = () => {
    const ticket = ++latest.current;
    clearLookup('employees');
    setError(''); setLoading(true);
    hr.employeeCompletenessPage({ ...params, skip: page.skip, limit: page.limit })
      .then(r => { if (ticket === latest.current) setData(r); })
      .catch(e => { if (ticket === latest.current) setError(errorText(e)); })
      .finally(() => { if (ticket === latest.current) setLoading(false); });
  };
  useEffect(load, [key, page.skip, page.limit]); // eslint-disable-line react-hooks/exhaustive-deps
  const runSearch = ev => {
    ev?.preventDefault();
    const term = q.trim();
    if (term === search && !page.skip) load(); else { setSearch(term); firstPage(); }
  };

  if (error && !data) return <div className="error">{error}</div>;
  if (!data) return <Loader title="Loading employees…" subtitle="Checking records for missing details" />;

  const shown = (data.items || []).map(e => ({ ...e, missing: (e.missing || []).map(m => [m.key, m.label]) }));
  const total = data.total || 0;
  const sm = data.summary || {};
  const count = k => sm.missing_counts?.[k] ?? 0;

  const sync = async () => {
    if (!(await confirm({ title: 'Sync from User Accounts', message: 'Create or link an employee record for every user account?', detail: 'Existing records are refreshed from their accounts; nothing is deleted.', confirmLabel: 'Sync' }))) return;
    setSyncing(true);
    try {
      const s = await hr.syncEmployeesFromUsers();
      notify(`${s.users} accounts: ${s.created} created, ${s.linked} linked, ${s.updated} refreshed${s.failed ? `, ${s.failed} failed` : ''}`, s.failed ? 'warn' : 'good');
      load();
    } catch (err) { notify(errorText(err), 'bad'); }
    setSyncing(false);
  };

  return (
    <div className="emp-master">
      <div className="e360-head">
        <div className="e360-head-left"><span className="back-label">Staff imported from user accounts start active with gaps — fill them in here, or open the full record.</span></div>
        <div className="e360-head-actions">
          <button className="icon-btn" title="Refresh" onClick={load}><RefreshCw size={16} /></button>
          {isSuperAdmin && <button className="btn" disabled={syncing} onClick={sync}><Users size={15} /> {syncing ? 'Syncing…' : 'Sync from User Accounts'}</button>}
        </div>
      </div>

      <LcKpis items={[
        ['Total Employees', sm.total ?? 0, 'blue', 'users'],
        ['Complete Records', sm.complete ?? 0, 'green', 'circle-check'],
        ['Incomplete Records', sm.incomplete ?? 0, 'amber', 'triangle-alert'],
        ['Missing Hire Date', count('hire_date'), 'red', 'calendar-x'],
        ['Missing Bank Account', count('bank'), 'purple', 'landmark'],
      ]} />

      <div className="panel me-table-panel">
        <div className="toolbar em-filters">
          <form className="me-search" onSubmit={runSearch}>
            <div className="search em-search"><Search size={16} /><input placeholder="Search name, ID, email…" value={q} onChange={e => setQ(e.target.value)} /></div>
            <button type="submit" className="btn primary" disabled={loading}><Search size={15} /> Search</button>
          </form>
          <Select value={missing} onChange={e => filter(setMissing)(e.target.value)}>
            <option value="">Any missing field</option>
            {CHECKS.map(([k, l]) => <option key={k} value={k}>Missing {l} ({count(k)})</option>)}
          </Select>
          <Select value={dept} onChange={e => filter(setDept)(e.target.value)}><option value="">All Departments</option>{departments.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}</Select>
          <button type="button" className={`btn ${onlyIncomplete ? 'danger-on' : ''}`} onClick={() => filter(setOnlyIncomplete)(!onlyIncomplete)} aria-pressed={onlyIncomplete}>
            {onlyIncomplete ? 'Showing incomplete only' : 'Showing all'}
          </button>
          <div className="spacer" />
          <span className="muted small">{total} employee{total === 1 ? '' : 's'}</span>
        </div>
        <div className="table-wrap register-wrap">
          <table className="me-table register-table">
            <thead><tr><th>Emp ID</th><th>Employee</th><th>Departments</th><th>Status</th><th>Completeness</th><th>Missing</th><th>Action</th></tr></thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7}><Loader title="Loading employees…" subtitle={`Page ${Math.floor(page.skip / page.limit) + 1} of ${Math.max(1, Math.ceil(total / page.limit))}`} /></td></tr>
              )}
              {!loading && shown.map(e => (
                <tr key={e.id}>
                  <td className="em-id">{e.employee_no}</td>
                  <td>
                    <div className="emp-cell">
                      <span className="emp-avatar">{initials(e.full_name)}</span>
                      <span><b>{e.full_name}</b><small>{e.user_email ? `Login: ${e.user_email}${e.user_is_active === false ? ' (disabled)' : ''}` : 'No login'}</small></span>
                    </div>
                  </td>
                  <td>{(e.departments || []).length ? (e.departments || []).map(x => x.name + (x.is_primary ? ' ★' : '')).join(', ') : <span className="muted">—</span>}</td>
                  <td><StatusPill value={e.employment_status} /></td>
                  <td><div className="dc-comp"><b>{e.percent}%</b><span><i style={{ width: `${e.percent}%` }} /></span></div></td>
                  <td>
                    <div className="warn-chips">
                      {!e.missing.length && <span className="muted small">Complete</span>}
                      {e.missing.slice(0, 3).map(([k, l]) => <span key={k} className="warn-chip">{l}</span>)}
                      {e.missing.length > 3 && <span className="warn-chip more" title={e.missing.slice(3).map(([, l]) => l).join(', ')}>+{e.missing.length - 3} more</span>}
                    </div>
                  </td>
                  <td>
                    <div className="row-actions">
                      {canManage && <button className="btn small primary" onClick={() => setEditing(e.id)}><Pencil size={13} /> Complete</button>}
                      <Link className="btn small" to={hrSubUrl('employee-360', { id: e.id })}>360</Link>
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && !shown.length && (
                <tr><td colSpan={7} className="empty">
                  {sm.total ? (search || dept || missing ? 'No employees match these filters.' : 'Every record in this view is complete.') : `No employee records yet.${isSuperAdmin ? ' Use "Sync from User Accounts" to create one for every user.' : ''}`}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        {error && <div className="error">{error}</div>}
        <Pagination total={total} skip={page.skip} limit={page.limit} onChange={setPage} />
      </div>
      <p className="muted small">{titleCase('checked fields')}: {CHECKS.map(([, l]) => l).join(' · ')}. Departments marked ★ are primary.</p>

      {editing && <QuickComplete employeeId={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
